import { format } from 'date-fns'

import {
    AppointmentFieldsFragment,
    AppointmentStatus,
} from '~graphql/generated/graphql'
import { getCleanEnum } from '~utils/helpers'

/** Lower number = higher priority in the appointment chip list. */
const APPOINTMENT_STATUS_SORT_ORDER: Record<AppointmentStatus, number> = {
    [AppointmentStatus.Confirmed]: 0,
    [AppointmentStatus.Booked]: 1,
    [AppointmentStatus.Pending]: 2,
    [AppointmentStatus.Completed]: 3,
    [AppointmentStatus.NoShow]: 4,
    [AppointmentStatus.Expired]: 5,
    [AppointmentStatus.Denied]: 6,
    [AppointmentStatus.Cancelled]: 7,
}

function getEffectiveAppointmentStatus(
    appointment: Pick<
        AppointmentFieldsFragment,
        'status' | 'is_deleted' | 'reason_for_cancellation'
    >,
): AppointmentStatus {
    return appointment.is_deleted
        ? AppointmentStatus.Cancelled
        : appointment.status
}

export function getAppointmentSelectLabel(
    appointment: AppointmentFieldsFragment,
): string {
    const status = getEffectiveAppointmentStatus(appointment)
    const serviceName =
        appointment.service?.name ?? appointment.service_name ?? 'Appointment'

    return `${getCleanEnum(status)} · ${serviceName} - ${format(
        appointment.start_time,
        'MMM d, h:mm a',
    )}`
}

/**
 * Active / upcoming statuses first, cancelled and other terminal statuses last.
 * Within the same status, newest start time first.
 */
export function sortAppointmentsForSelect(
    appointments: AppointmentFieldsFragment[],
): AppointmentFieldsFragment[] {
    return [...appointments].sort((a, b) => {
        const statusA = getEffectiveAppointmentStatus(a)
        const statusB = getEffectiveAppointmentStatus(b)
        const statusDiff =
            APPOINTMENT_STATUS_SORT_ORDER[statusA] -
            APPOINTMENT_STATUS_SORT_ORDER[statusB]

        if (statusDiff !== 0) return statusDiff

        return b.start_time - a.start_time
    })
}
